import { useState } from 'react'
import { Botao } from '../../../components/Botao'
import { CampoDeTextoLongo } from '../../../components/campos'
import { AvisoDeErro } from '../../../components/Estados'

interface Props {
  rotulo: string
  ajuda: string
  valor: string | null
  /** Pista de UI: quando falso, a tela não oferece o botão. Quem autoriza é o servidor, no PATCH. */
  editavel: boolean
  editando: boolean
  salvando: boolean
  erro: unknown
  aoEditar: () => void
  aoCancelar: () => void
  aoSalvar: (texto: string) => void
}

/**
 * Um artefato do plano: o que está escrito, e como corrigir sem sair da tela.
 *
 * Campo vazio aparece dito com todas as letras, em vez de sumir. Um plano com seções faltando é
 * exatamente o que o comitê precisa enxergar antes de aprovar — se a tela esconder o que está
 * vazio, a RDM parece completa.
 *
 * O texto sai com `whitespace-pre-line`: a pessoa escreve o passo a passo em linhas, e colapsar
 * as quebras transformaria um roteiro de implantação num parágrafo único.
 *
 * Não é `<dl>` como no resto das telas de detalhe: aqui o mesmo campo é rótulo na leitura e
 * `<label>` de formulário na edição, e as duas marcações juntas fariam o leitor de tela anunciar
 * o rótulo duas vezes. Com `<h3>`, quem navega por cabeçalhos ainda pula de artefato em artefato.
 *
 * @param props o rótulo, a ajuda, o valor e o estado da edição
 * @returns o artefato do plano
 */
export function CampoDoPlanoEditavel(props: Props) {
  const { rotulo, ajuda, valor, editavel, editando, salvando, erro, aoEditar, aoCancelar, aoSalvar } = props

  if (editando) {
    return (
      <div className="border-t border-slate-100 pt-5 first:border-0 first:pt-0">
        <Editor
          rotulo={rotulo}
          ajuda={ajuda}
          valor={valor}
          salvando={salvando}
          erro={erro}
          aoSalvar={aoSalvar}
          aoCancelar={aoCancelar}
        />
      </div>
    )
  }

  return (
    <div className="border-t border-slate-100 pt-5 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800">{rotulo}</h3>
        {editavel && (
          <Botao tamanho="pequeno" onClick={aoEditar}>
            Editar
            {/* Oito botões escritos "Editar" na mesma tela: sem isto, quem ouve a página não
                distingue um do outro ao percorrer os controles. */}
            <span className="sr-only"> {rotulo}</span>
          </Botao>
        )}
      </div>
      <p className="mt-0.5 text-xs text-slate-500">{ajuda}</p>
      <p className={valor ? 'mt-2 whitespace-pre-line text-sm text-slate-700' : 'mt-2 text-sm italic text-slate-400'}>
        {valor || 'Ainda não preenchido'}
      </p>
    </div>
  )
}

/**
 * O campo aberto para edição.
 *
 * Componente à parte para o rascunho nascer do valor gravado a cada abertura: mantido no
 * componente de cima, ele guardaria o texto de uma edição cancelada e o devolveria na próxima —
 * o usuário reabriria o campo e veria de volta o que tinha desistido de escrever.
 *
 * @param props rótulo, ajuda, valor gravado, estado do envio e os desfechos
 * @returns o editor do artefato
 */
function Editor({
  rotulo,
  ajuda,
  valor,
  salvando,
  erro,
  aoSalvar,
  aoCancelar,
}: Omit<Props, 'editavel' | 'editando' | 'aoEditar'>) {
  const [texto, setTexto] = useState(valor ?? '')

  return (
    <div className="space-y-3">
      <CampoDeTextoLongo
        rotulo={rotulo}
        ajuda={ajuda}
        rows={8}
        value={texto}
        onChange={(evento) => setTexto(evento.target.value)}
        // Quem clicou em "Editar" quer escrever agora. Sem o foco aqui, quem usa teclado teria
        // de voltar até o campo que acabou de abrir.
        autoFocus
      />
      {Boolean(erro) && <AvisoDeErro erro={erro} titulo="Não foi possível salvar" />}
      <div className="flex flex-wrap gap-2">
        <Botao aparencia="primario" disabled={salvando} onClick={() => aoSalvar(texto)}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Botao>
        <Botao disabled={salvando} onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </div>
  )
}
